import { SellerFilterOption } from '@/components/SellerFilter/SellerFilterUi';
import cacheKeys from '@/data/api/cacheKeys';
import { getProducts } from '@/data/api/request/productCatalogRequests';
import { responseToPagingUi } from '@/hooks/pagingMapper';
import { useGetProductCatalog } from '@/hooks/product/catalog/useGetProducts';
import { productsResponseToProductsUi } from '@/hooks/product/catalog/useGetProducts/mapper';
import { ProductState } from '@/hooks/product/catalog/useGetProducts/productState';
import { useTableSearchInPlace } from '@/hooks/useTableSearch';
import { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { initialState, ProductsUi } from './ProductUi';

export function useProductsViewController(state?: ProductState) {
  const [uiState, setUiState] = useImmer<ProductsUi>(initialState);
  const [currentPage, setCurrentPage] = useState(1);
  const [sellerFilter, setSellerFilter] = useState<SellerFilterOption[]>();

  const { data: productsData, isLoading } = useGetProductCatalog(
    currentPage,
    state,
    sellerFilter
  );

  const tableSearchHook = useTableSearchInPlace(
    cacheKeys.product.catalog,
    uiState.searchQuery,
    {
      searchKey: [cacheKeys.product.catalogSearch, { sellerFilter }],
      queryFunction: (searchTerm) =>
        getProducts({
          searchTerm,
          state,
          sellerIds: sellerFilter?.map((item) => item.sellerId),
        }),
      mapper: (response) => productsResponseToProductsUi(response),
    }
  );

  const onChangedSellerFilter = (sellerFilter: SellerFilterOption[]) =>
    setSellerFilter(sellerFilter);

  useEffect(() => {
    setUiState((draft) => {
      draft.isLoading = isLoading;
      if (draft.isLoading) {
        draft.paging = undefined;
        draft.products = [];
      }
    });
  }, [isLoading]);

  const populatePaginatedTable = () => {
    if (!productsData) return;
    setUiState((prev) => {
      prev.paging = responseToPagingUi(productsData);
      prev.products = productsResponseToProductsUi(productsData);
    });
  };

  useEffect(() => populatePaginatedTable(), [productsData]);

  useEffect(() => {
    setUiState((prev) => {
      if (!tableSearchHook.searchResults) {
        populatePaginatedTable();
        return;
      }

      prev.isSearching = tableSearchHook.isLoading;
      prev.products = tableSearchHook.searchResults ?? [];
      prev.paging = undefined;
    });
  }, [tableSearchHook.searchResults, tableSearchHook.isLoading]);

  const onPageRequested = (page: number) => setCurrentPage(page);

  const onSearchChanged = (value: string) =>
    setUiState((prev) => {
      prev.searchQuery = value;
    });

  useEffect(() => {
    setCurrentPage(1);
  }, [state, sellerFilter]);

  useEffect(() => {
    setUiState((draft) => {
      draft.searchQuery = '';
    });
  }, [state]);

  return {
    uiState,
    actions: {
      onPageRequested,
      onSearchChanged,
      onChangedSellerFilter,
    },
  };
}
